import ResourceClient from "dea-bookmarks-clients/src/clients/resources";

/**
 * Class that handles the basic bookmark load / save / delete actions.
 *
 * emits `save-bookmark`, `delete-bookmark` events after triggering resource client
 * to allow other parts of the application to sync with the changes
 * (like adding / removing items in vuex)
 *
 * To implement this mixin:
 * - define `this.bookmarksSave` as the name of the ResourceClient method that should be used to save the bookmark
 * - define `this.bookmarksGet` as the name of the ResourceClient method that should be used to load the bookmarks
 * - define `this.bookmarkType` as the name of the attribute holding bookmarks in ResourceClient.
 *
 * Example:
 *
 *   data() {
 *      return {
 *        bookmarksSave: "saveResource",
 *        bookmarksGet: "getResources",
 *        bookmarkType: "resource",
 *      }
 *    }
 *
 * Site appConf.js must provide a `bookmarkOrigin` object including code and label.
 *
 * Example:
 *
 *    bookmarkOrigin: {
 *      label: "Area Personale",
 *      code: "myhome",
 *    }
 *
 *
 * ### Enable bookmarks based on configuration option
 *
 * Bookmark save behavior can be passed to bookmarkable components by prop and by relying on a configuration option or a computed.
 *
 * You **must** implement the `bookmarkEnabled` function and you **must** make sure you have either a "local" user reference or a global user refence (see [Header User documentation]( https://clients.nephi.la/deascuola/dea-design-system-storybook/?path=/story/design-system-intro-header-user--page)).
 *
 * Example:
 *
 *
 *  <CardResource
 *    v-for="item in items"
 *    ...
 *    :bookmark-enabled="bookmarkEnabled"
 *    ...
 *  />
 *  ...
 *  computed: {
 *    bookmarkEnabled() {
 *      // when using local user reference
 *      return this.config ? this.config.bookmarks_resource_enabled && this.user.authenticated : null;
 *       // when using global user reference
 *      return this.config ? this.config.bookmarks_resource_enabled && this.$root.user.authenticated : null;
 *    },
 *    ...
 *  }
 *
 * ### Passing the bookmark status to the bookmarkable component
 *
 * Mixin provides the list of available bookmarks on the this.bookmarks object, indexed by the data_obj_id attribute.
 *
 * This makes easy to check if a resource is bookmarked or not and passing this information to the bookmarkable component.
 *
 * Example:
 *
 *  <CardResource
 *    v-for="item in items"
 *    ...
 *    :bookmark="bookmarks[item.resource_uuid]"
 *    ...
 *  />
 *
 * ### Methods
 *
 * #### saveBookmark
 *
 * It receives a `resource` as argument
 * This argument depends on which kind of resource needs to be bookmarked (DAM resource, DeA Live post, etc.)
 * i.e. the DAM resource object as it is described here https://gitix.iast.it/clients/deascuola/dea-bookmarks-client/-/blob/master/src/clients/resources.js#L136)
 *
 * #### deleteBookmark
 *
 * It receives a bookmark as argument
 * Usually it is `bookmarks[item.resource_uuid]`
 *
 * ### Troubleshooting
 *
 * If activating a bookmark other resources are bookmarked at the same time, it is because there are empty resource_uuids (`item.resource_uuid` is null).
 *
 */
export default {
  data() {
    let bookmarkOrigin;
    if (this.$root.config.bookmarkOrigin) {
      bookmarkOrigin = this.$root.config.bookmarkOrigin;
      bookmarkOrigin.url = document.location.origin;
    }
    return {
      bookmarks: {},
      bookmarkOrigin,
      bookmarkType: null,
    };
  },
  created() {
    this.loadBookmarks();
  },
  computed: {
    token() {
      if (this.$cookies) return this.$cookies.get("deatoken");
      return null;
    },
  },
  methods: {
    loadBookmarks() {
      if (this.token) {
        ResourceClient[this.bookmarksGet](this.token).then(resources => {
          resources.forEach(item => {
            this.$set(this.bookmarks, item.data_obj_id, item);
          });
        });
      }
    },
    saveBookmark(resource) {
      if (this.token) {
        ResourceClient[this.bookmarksSave](this.token, resource, this.bookmarkOrigin).then(bookmark => {
          this.$set(this.bookmarks, bookmark.data_obj_id, bookmark);
          // this event is used by upper components using vuex to keep the state consistent (if needed)
          this.$emit("save-bookmark", this.bookmarkType, bookmark);
        });
      }
    },
    deleteBookmark(bookmark) {
      if (this.token) {
        ResourceClient.deleteObject(this.token, bookmark.id).then(() => {
          this.$delete(this.bookmarks, bookmark.data_obj_id);
          // this event is used by upper components using vuex to keep the state consistent (if needed)
          this.$emit("delete-bookmark", this.bookmarkType, bookmark);
        });
      }
    },
    filterBookmarksByOrigin(origin) {
      if (this.token) {
        return ResourceClient.filterByOrigin(this.token, origin).then(resources => {
          return resources;
        });
      }
      return [];
    },
  },
};
